import type {} from '@mui/lab/themeAugmentation';
import {
  alpha,
  ComponentsOverrides,
  ComponentsProps,
  ComponentsVariants,
  createTheme,
} from '@mui/material/styles';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import React from 'react';

import {
  IGallery,
  TGalleryClassKey,
} from 'components/system/Gallery/gallery.types';
import { colors, gradients } from './colors';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    subtitle: React.CSSProperties;
    textXl: React.CSSProperties;
    textBase: React.CSSProperties;
    textSm: React.CSSProperties;
    textXs: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    subtitle: React.CSSProperties;
    textXl: React.CSSProperties;
    textBase: React.CSSProperties;
    textSm: React.CSSProperties;
    textXs: React.CSSProperties;
  }

  interface PaletteColor {
    primary: string;
    secondary: string;
    tertiary: string;
    quaternary: string;

    error: string;
    success: string;
    warning: string;
    info: string;
    danger: string;
  }

  interface SimplePaletteColorOptions {
    primary?: string;
    secondary?: string;
    tertiary?: string;
    quaternary?: string;

    error?: string;
    success?: string;
    warning?: string;
    info?: string;
    danger?: string;
  }

  interface Palette {
    colors: typeof colors;
    gradients: typeof gradients;
    border: Palette['primary'];
    box: Palette['primary'];
    title: Palette['primary'];
    icon: Palette['primary'];
    gradient: Palette['primary'];
    galleryOverlay: Palette['primary'];
  }
  interface PaletteOptions {
    colors: typeof colors;
    gradients: typeof gradients;
    border: PaletteOptions['primary'];
    box: PaletteOptions['primary'];
    title: PaletteOptions['primary'];
    icon: PaletteOptions['primary'];
    gradient: PaletteOptions['primary'];
    galleryOverlay: PaletteOptions['primary'];
  }

  interface ComponentsPropsList {
    GalleryContainer: IGallery<object>;
  }
  interface ComponentNameToClassKey {
    GalleryContainer: TGalleryClassKey;
  }
  interface Components {
    GalleryContainer?: {
      defaultProps?: ComponentsProps['GalleryContainer'];
      styleOverrides?: ComponentsOverrides['GalleryContainer'];
      variants?: ComponentsVariants['GalleryContainer'];
    };
  }
}

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    main: string;
    primary: string;
    secondary: string;
    tertiary: string;
    quaternary: string;
  }

  interface TypeText {
    primary: string;
    secondary: string;
    tertiary: string;
    info: string;
    muted: string;
    error: string;
    danger: string;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    subtitle: true;
    textXl: true;
    textBase: true;
    textSm: true;
    textXs: true;
  }
}

const theme = (_mode = 'dark') =>
  createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1600,
      },
    },
    spacing: [
      '0', // 0
      '0.125rem', // 1 -> 2px
      '0.25rem', // 2 -> 4px
      '0.375rem', // 3 -> 6px
      '0.5rem', // 4 -> 8px
      '0.75rem', // 5 -> 12px
      '1rem', // 6 -> 16px
      '1.25rem', // 7 -> 20px
      '1.5rem', // 8 -> 24px
      '2rem', // 9 -> 32px
      '2.5rem', // 10 -> 40px
      '3rem', // 11 -> 48px
      '4rem', // 12 -> 64px
      '5rem', // 13 -> 80px
      '6rem', // 14 -> 96px
      '8rem', // 15 -> 128px
      '10rem', // 16 -> 160px
    ],
    palette: {
      common: {
        white: colors.white,
        black: colors.black,
      },
      colors,
      gradients,
      primary: {
        light: colors.secondary,
        main: colors.primary,
        dark: colors.darkerPrimary,
      },
      text: {
        primary: colors.white,
        secondary: colors.lightGrey1,
        tertiary: colors.lightGrey2,
        info: colors.lightGrey3,
        muted: colors.lightGrey4,
        error: colors.lightRed,
        danger: colors.lightPink,
      },
      border: {
        main: colors.primary,
        primary: colors.primary,
        secondary: colors.lightGrey5,
        tertiary: colors.shallowPrimary,
        error: colors.shallowRed,
        success: colors.shallowGreen,
        warning: colors.shallowYellow,
        info: colors.shallowBlue,
      },
      title: {
        main: colors.lightGrey3,
      },
      background: {
        main: colors.darkGrey1,
        primary: colors.darkGrey1,
        secondary: colors.darkGrey2,
        tertiary: colors.darkGrey3,
        quaternary: colors.darkGrey4,
        paper: colors.darkGrey4, // toast
      },
      galleryOverlay: {
        main: colors.shallowGrey1,
        primary: colors.shallowGrey1,
        secondary: colors.shallowPrimary,
        tertiary: colors.shallowGrey2,
      },
      box: {
        main: colors.darkGrey5,
        primary: colors.darkGrey5,
        secondary: colors.darkGrey6,
        tertiary: colors.darkGrey7,
        quaternary: colors.darkGrey8,
        error: colors.darkRed1,
        success: colors.darkGreen,
      },
      icon: {
        main: colors.primary,
        primary: colors.primary,
        secondary: colors.lightGrey5,
        dark: colors.darkPrimary,
      },
      gradient: {
        main: gradients.gradientD,
      },
    },
    typography: {
      htmlFontSize: 16,
      fontFamily: '"Roboto", "Arial", sans-serif',
      body1: {
        fontSize: '0.875rem',
      },
      body2: {
        fontSize: '0.875rem',
        color: colors.primary,
      },
      h1: {
        fontSize: '3rem',
        fontWeight: 'bold',
        lineHeight: '3.75rem',
        letterSpacing: '-2%',
      },
      h2: {
        fontSize: '1.875rem',
        fontWeight: 'bold',
        lineHeight: '2.375rem',
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 'bold',
        color: colors.lightGrey3,
        lineHeight: '2rem',
      },
      h4: {
        fontSize: '1.25rem',
        color: colors.lightGrey3,
        lineHeight: '1.875rem',
      },
      h5: {
        fontSize: '1.125rem',
        lineHeight: '1.75rem',
      },
      h6: {
        fontSize: '1rem',
        lineHeight: '1.5rem',
      },
      textXl: {
        fontSize: '1rem',
        lineHeight: '1.5rem',
      },
      textBase: {
        fontSize: '0.875rem',
        lineHeight: '1.25rem',
      },
      textSm: {
        fontSize: '0.75rem',
        lineHeight: '1rem',
      },
      textXs: {
        fontSize: '0.625rem',
        lineHeight: '0.875rem',
      },
      subtitle: {
        fontSize: '0.75rem',
      },
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            color: 'white',
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${colors.primary}`,
            marginBottom: 0,
          },
          underline: {
            '& :after': {
              borderBottomColor: colors.primary,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&:hover fieldset': {
              borderColor: 'yellow',
            },
            '& .Mui-focused fieldset': {
              borderColor: 'green',
            },
          },
          input: {
            color: colors.lightGrey2,
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: colors.primary,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.darkGrey2,
            color: colors.lightGrey1,
            boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.25)',
          },
          option: {
            backgroundColor: colors.darkGrey2,
            '&.Mui-focused': {
              backgroundColor: `${alpha(colors.primary, 0.08)}!important`,
            },
            '&[aria-selected="true"]': {
              backgroundColor: `${alpha(colors.primary, 0.2)}!important`,
            },
          },
          noOptions: {
            color: colors.lightGrey1,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { color: colors.primary },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: { color: colors.lightGrey2 },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { color: colors.primary },
        },
      },
      MuiImageListItemBar: {
        styleOverrides: {
          titleWrapBelow: {
            padding: '20px 10px',
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            backgroundColor: colors.darkGrey2,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          flexContainer: {
            borderBottom: `1px solid ${colors.lightGrey2}`,
          },
          indicator: {
            backgroundColor: colors.primary,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: colors.lightGrey2,
            textTransform: 'none',
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            borderCollapse: 'separate',
            borderSpacing: '0 2px',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: 'none',
            boxSizing: 'border-box',
            fontSize: 16,
            maxHeight: 50,
          },
          stickyHeader: {
            backgroundColor: 'transparent',
            fontSize: 20,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            boxSizing: 'border-box',
            maxWidth: 50,
          },
        },
      },
    },
  });

export default theme;
